// The chips on "What do you want?" — made from the card, never from a list.
//
// A chip is a thing on today's board the punter might want, in their words:
// "NBL tonight", "Horses", "Premier League". Tapping one says "include this";
// tapping again cycles it on to "only this" and then "not this"; the state is
// written on the chip. Long-press opens the full set, "prefer" included.
//
// Pure: boards and a clock in, chips out. The request the chips make is built
// here too (`requestFrom`), so the screen holds selections and nothing else.

import { eventLabel, sportMatches, normaliseRequest, SPORT_LEAD_MS } from '../engine/ask.js';
import { RACING_LEAD_MS } from '../engine/strategies/racing.js';
import { isRacingSport, sportLabel } from '../engine/teams.js';
import { bestPriceToTake } from '../engine/universe.js';

/** Chip weight → request weight. "include" is the punter's word for must. */
export const CHIP_STATES = ['include', 'only', 'prefer', 'avoid'];
export const TO_WEIGHT = { include: 'must', only: 'only', prefer: 'prefer', avoid: 'avoid' };
export const STATE_WORD = { include: 'Include', only: 'Only', prefer: 'Lean', avoid: 'Not' };

/** What a tap does. An event cannot be "only": a multi has one leg per event. */
export function nextState(chip, state) {
  const cycle = chip.type === 'event' ? [null, 'include', 'avoid'] : [null, 'include', 'only', 'avoid'];
  const i = cycle.indexOf(state ?? null);
  return cycle[(i + 1) % cycle.length];
}

const TZ = 'Australia/Sydney';
const hourIn = (ms) => Number(new Date(ms).toLocaleString('en-AU', { timeZone: TZ, hour: '2-digit', hourCycle: 'h23' }));
const dayIn = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: TZ });
const weekday = (ms) => new Date(ms).toLocaleDateString('en-AU', { timeZone: TZ, weekday: 'short' });
const clock = (ms) => new Date(ms).toLocaleTimeString('en-AU', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
  .replace(' ', '').toLowerCase();

/** "tonight", "this arvo", "Sat" — when a group of starts is, in a word. */
function whenWord(starts, asOfMs) {
  const today = dayIn(asOfMs);
  if (starts.every((s) => dayIn(s) !== today)) return weekday(starts[0]);
  if (starts.every((s) => hourIn(s) >= 17)) return 'tonight';
  if (starts.every((s) => hourIn(s) < 12)) return 'this morning';
  return null;
}

/**
 * The chips for a board.
 *
 * Events: every sports fixture still to start, grouped by sport then start,
 * each with its two sides so a leg can be locked from it. Sports: one per sport on the board with a
 * count, racing as its codes plus "Racing" for all three. Nothing that has
 * already started is ever a chip.
 */
export function chipsFor(boards, { asOf }) {
  const asOfMs = Date.parse(asOf);
  const events = [];
  const bySport = new Map();
  for (const b of boards) {
    const universe = b.universe ?? b;
    for (const match of universe.matches.values()) {
      const start = Date.parse(match.kickoff);
      const lead = match.runners ? RACING_LEAD_MS : SPORT_LEAD_MS;
      if (!(start - asOfMs >= lead)) continue;
      if (!bySport.has(match.sport)) bySport.set(match.sport, []);
      bySport.get(match.sport).push(start);
      if (match.runners) continue;
      events.push({
        key: `event:${match.matchKey}`,
        type: 'event',
        value: match.matchKey,
        label: eventLabel(match),
        sport: match.sport,
        sportLabel: match.sportTitle ?? match.competition ?? sportLabel(match.sport),
        kickoff: match.kickoff,
        sub: clock(start),
        // Both sides as legs the punter can lock: `${matchKey}:home` is the key
        // the engine's `include` takes.
        sides: ['home', 'away'].filter((side) => match.prices?.[side]?.length).map((side) => ({
          key: `${match.matchKey}:${side}`,
          name: match[side]?.name ?? side,
          // The longest price a punter could walk in and take, so a side reads
          // as a bet ("Brisbane 1.68") rather than as a team name.
          price: bestPriceToTake(match, side)?.price ?? null,
        })),
      });
    }
  }
  events.sort((a, b) => a.sport.localeCompare(b.sport) || a.kickoff.localeCompare(b.kickoff) || a.key.localeCompare(b.key));

  const sports = [];
  for (const [sport, starts] of [...bySport.entries()].sort()) {
    starts.sort((a, b) => a - b);
    const word = whenWord(starts, asOfMs);
    sports.push({
      key: `sport:${sport}`,
      type: 'sport',
      value: sport,
      label: word ? `${sportLabel(sport)} ${word}` : sportLabel(sport),
      count: starts.length,
      racing: isRacingSport(sport),
    });
  }
  const racingCount = sports.filter((s) => s.racing).reduce((n, s) => n + s.count, 0);
  if (sports.filter((s) => s.racing).length > 1) {
    sports.push({ key: 'sport:racing', type: 'sport', value: 'racing', label: 'Racing', count: racingCount, racing: true });
  }
  // Sports before racing, and the busiest of each first.
  sports.sort((a, b) => Number(a.racing) - Number(b.racing) || (a.value === 'racing') - (b.value === 'racing')
    || b.count - a.count || a.key.localeCompare(b.key));
  return { events, sports };
}

// What the builder offers. The engine still answers 'now' (next 3 hours) and
// 'outrageous', which saved asks and presets (Classic Four Ways) can carry;
// neither is a choice on the screen.
export const WHEN_CHOICES = [
  { value: 'today', label: 'Today' },
  { value: 'tonight', label: 'Tonight' },
  { value: 'tomorrow', label: 'Tomorrow' },
];
export const RISK_CHOICES = [
  { value: null, label: 'Any' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
export const LEG_CHOICES = [null, 2, 3, 4, 5, 6, 8];

/**
 * The request a set of selections makes.
 *
 * selections: { chips: { [chipKey]: state }, when, risk, legs, odds, search, strategy }
 * `chips` keys are the chip keys above (`sport:afl`, `event:<matchKey>`); an
 * event chip carries its label and kickoff into the request, so a match that
 * starts while the chip is lit can still be named when it says so.
 */
export function requestFrom(selections = {}, chips = { events: [], sports: [] }) {
  const sports = [];
  const events = [];
  const find = new Map([...chips.events, ...chips.sports].map((c) => [c.key, c]));
  for (const [key, state] of Object.entries(selections.chips ?? {}).sort()) {
    if (!state || !TO_WEIGHT[state]) continue;
    const [type, ...rest] = key.split(':');
    const value = rest.join(':');
    if (type === 'sport') sports.push({ sport: value, weight: TO_WEIGHT[state] });
    if (type === 'event') {
      const chip = find.get(key);
      events.push({
        event: value,
        weight: TO_WEIGHT[state] === 'only' ? 'must' : TO_WEIGHT[state],
        ...(chip ? { label: chip.label, kickoff: chip.kickoff } : {}),
      });
    }
  }
  return normaliseRequest({
    sports,
    events,
    when: selections.when ?? 'today',
    risk: selections.risk ?? null,
    legs: selections.legs ?? undefined,
    odds: selections.odds ?? null,
    search: selections.search ?? '',
    strategy: selections.strategy ?? 'best',
    include: selections.include ?? [],
    exclude: selections.exclude ?? [],
  });
}

/** Is any chip saying anything? The default answer needs no opinion at all. */
export const hasOpinion = (selections) => Object.values(selections?.chips ?? {}).some(Boolean)
  || (selections?.include?.length ?? 0) > 0
  || !!selections?.risk || !!selections?.legs || !!selections?.odds || !!selections?.search
  || (!!selections?.when && selections.when !== 'today');

/** The games on the board, grouped by sport, for the "Pick games" list. */
export function gamesBySport(chips) {
  const groups = new Map();
  for (const e of chips.events) {
    if (!groups.has(e.sport)) groups.set(e.sport, { sport: e.sport, label: e.sportLabel, games: [] });
    groups.get(e.sport).games.push(e);
  }
  return [...groups.values()];
}

/** Every chip the board can make, matched against a sport id, for "Racing" and friends. */
export const chipsForSport = (chips, id) => chips.sports.filter((c) => sportMatches(id, c.value));
